// Signatuurblokken met keuzes: hotspot, inkleuren, wisselen, kopjes, temperatuur en bruiningsstand.
// Alle teksten en standen staan al in de HTML (snippets/signature-*.liquid). Dit script wisselt alleen:
// knoppen (aria-pressed), uitleg (hidden), foto's, spot, kleur, display en filter.

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class SignatureChoice extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-choice]');
      if (!button || !this.contains(button)) return;
      if (button.dataset.preset) this.applyPreset(button);
      else this.choose(button);
    });

    this.querySelectorAll('[data-range]').forEach((input) => {
      input.addEventListener('input', () => {
        this.activate(input.dataset.group, String(Number(input.value) - 1), input);
      });
    });

    this.syncPresets();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.countFrame);
  }

  choose(button) {
    const optional = button.closest('[data-optional]');
    const deselect = optional && button.getAttribute('aria-pressed') === 'true';
    this.activate(button.dataset.group, deselect ? null : button.dataset.index);
    this.syncPresets();
  }

  activate(group, index, range) {
    this.querySelectorAll(`[data-choice][data-group="${group}"]`).forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.index === index));
    });

    const panel = index === null ? null : this.querySelector(`[data-panel="${index}"]`);
    this.showPanel(group, panel);
    this.moveSpot(group, panel);
    this.swapImage(index);
    this.tint(group, panel);
    this.count(group, panel);
    this.filter(panel);

    if (range) {
      this.querySelectorAll('[data-range-value]').forEach((el) => {
        el.textContent = range.value;
      });
      const text = panel?.querySelector('[data-valuetext]')?.textContent.trim();
      if (text) range.setAttribute('aria-valuetext', text);
    }
  }

  showPanel(group, panel) {
    const out = this.querySelector('[data-panels]');
    if (!out) return;
    const lastOnly = out.dataset.panels === 'last';
    out.querySelectorAll('[data-panel]').forEach((item) => {
      if (lastOnly || item.dataset.group === group) item.hidden = item !== panel;
    });
    if (!panel && lastOnly) {
      const current = this.querySelector('[data-choice][aria-pressed="true"]');
      const fallback = current && this.querySelector(`[data-panel="${current.dataset.index}"]`);
      if (fallback) fallback.hidden = false;
    }
  }

  moveSpot(group, panel) {
    const spot = this.querySelector(`[data-spot="${group}"]`);
    if (!spot) return;
    if (!panel || panel.dataset.x === undefined) {
      spot.hidden = true;
      return;
    }
    const { x, y, w, h } = panel.dataset;
    spot.style.left = `${x}%`;
    spot.style.top = `${y}%`;
    spot.style.width = `${w}%`;
    spot.style.height = `${h}%`;
    spot.hidden = false;
    const label = spot.querySelector('[data-spot-label]');
    if (label) label.textContent = panel.dataset.label || '';
  }

  swapImage(index) {
    const layers = this.querySelectorAll('[data-image]');
    if (!layers.length || index === null) return;
    const target = this.querySelector(`[data-image="${index}"]`) || this.querySelector('[data-image="base"]');
    if (!target) return;
    layers.forEach((layer) => layer.classList.toggle('is-off', layer !== target));
  }

  tint(group, panel) {
    if (!panel?.dataset.color) return;
    this.style.setProperty(`--tint-${group}`, panel.dataset.color);
    const label = this.querySelector(`[data-tint-label="${group}"] span`);
    if (label) label.textContent = panel.dataset.label || '';
  }

  count(group, panel) {
    const el = this.querySelector(`[data-count="${group}"]`);
    if (!el || !panel || panel.dataset.number === undefined) return;
    cancelAnimationFrame(this.countFrame);
    const to = panel.dataset.number;
    const from = el.textContent.trim();
    if (!/^\d+$/.test(to) || !/^\d+$/.test(from) || reducedMotion()) {
      el.textContent = to;
      return;
    }
    const a = Number(from);
    const b = Number(to);
    const duration = Math.min(900, Math.abs(b - a) * 18);
    const start = performance.now();
    const step = (now) => {
      const t = duration ? Math.min(1, (now - start) / duration) : 1;
      el.textContent = String(Math.round(a + (b - a) * t));
      if (t < 1) this.countFrame = requestAnimationFrame(step);
    };
    this.countFrame = requestAnimationFrame(step);
  }

  filter(panel) {
    if (!panel || panel.dataset.filter === undefined) return;
    const target = this.querySelector('[data-filter-target]');
    if (target) target.style.filter = panel.dataset.filter;
  }

  applyPreset(button) {
    const [a, b] = button.dataset.preset.split(',');
    this.activate('0', `0-${a}`);
    this.activate('1', `1-${b}`);
    this.syncPresets();
  }

  syncPresets() {
    const presets = this.querySelectorAll('[data-preset]');
    if (!presets.length) return;
    const current = ['0', '1']
      .map((group) => {
        const pressed = this.querySelector(`[data-choice][data-group="${group}"][aria-pressed="true"]`);
        return pressed ? pressed.dataset.index.split('-')[1] : '';
      })
      .join(',');
    presets.forEach((preset) => {
      const on = preset.dataset.preset === current;
      preset.setAttribute('aria-pressed', String(on));
      const panel = this.querySelector(`[data-panel="${preset.dataset.index}"]`);
      if (panel) panel.hidden = !on;
    });
  }
}

['signature-hotspot', 'signature-tint', 'signature-switcher', 'signature-cups', 'signature-temperature', 'signature-toaster'].forEach(
  (name) => {
    if (!customElements.get(name)) customElements.define(name, class extends SignatureChoice {});
  }
);
